export const Role = {
  SuperAdmin: 'SuperAdmin',
  Admin: 'Admin',
  LeagueAdmin: 'LeagueAdmin',
  MediaManager: 'MediaManager',
  LeagueDirector: 'LeagueDirector',
  LeagueCurator: 'LeagueCurator',
  ClubAdmin: 'ClubAdmin',
  ClubOfficer: 'ClubOfficer',
  User: 'User',
  Anonymous: 'Anonymous',
} as const;
export type Role = (typeof Role)[keyof typeof Role];

/**
 * Every role defined in the system, including the unauthenticated `Anonymous`
 * role. Derived from `Role` so new roles are picked up automatically.
 */
export const ALL_ROLES: Role[] = Object.values(Role);

/**
 * Every role that represents a logged-in user — i.e. all roles except the
 * unauthenticated `Anonymous`. Use this wherever a nav item or feature must be
 * visible to ALL authenticated users regardless of their specific role (e.g. the
 * read-only Permissions Matrix), so the list stays correct as new roles are added.
 */
export const ALL_AUTHENTICATED_ROLES: Role[] = ALL_ROLES.filter((role) => role !== Role.Anonymous);

/**
 * Per-user settings for how the CMS behaves for that account. Stored on the user document rather
 * than in browser storage, so a preference set on a laptop also holds on a phone.
 */
export type UserPreferences = {
  /**
   * When `false`, no tutorial ever starts on its own for this user — the admin app skips the
   * auto-show of the first incomplete tutorial. Tutorials stay fully available to launch by hand
   * from `/help`; this governs only the automatic ones.
   *
   * Optional because storage cannot guarantee it: preferences are persisted field-by-field with a
   * dot-path `$set`, which applies no schema defaults, so a document can hold some preferences and
   * not others. Read it through `areTutorialsEnabled` rather than directly.
   */
  tutorialsEnabled?: boolean;
};

/**
 * Applied wherever a user has no `preferences` record yet. Every account predates the field, so
 * the defaults have to reproduce the previous behaviour: tutorials auto-show.
 */
export const DEFAULT_USER_PREFERENCES: Required<UserPreferences> = {
  tutorialsEnabled: true,
};

/**
 * Whether tutorials may start automatically for this user. Opt-out rather than opt-in: only an
 * explicit `false` disables them, so a user with no `preferences` — or a partially populated one —
 * keeps auto-showing tutorials exactly as before.
 */
export const areTutorialsEnabled = (user?: { preferences?: Partial<UserPreferences> | null } | null): boolean =>
  user?.preferences?.tutorialsEnabled !== false;

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  avatar: string;
  access?: {
    clubs: {
      update: string[];
    };
  };
  clubShortNames?: string[];
  preferences?: UserPreferences;
  /**
   * Marks an E2E test account (Phase 0b). Server-only: no API request may set it — see
   * `apps/api/src/users/visible-users.ts` — so it only ever arrives on a document already read
   * from the database, never on a create/update payload built by a client.
   */
  isTestAccount?: boolean;
};

export type UserTokenData = {
  id: string;
  email: string;
  role: Role;
  firstName?: string;
  lastName?: string;
  access?: {
    clubs: {
      update: string[];
    };
  };
  /** Carried onto the JWT so `TestAccountWriteLockInterceptor` and `FilesController.recordView` can read it off the token alone, without a DB lookup. */
  isTestAccount?: boolean;
};

export type JwtToken = {
  user: UserTokenData;
  iat: number;
  exp: number;
};

export const Gender = {
  Male: 'male',
  Female: 'female',
} as const;
export type Gender = (typeof Gender)[keyof typeof Gender];

/**
 * Type guard for a value that is genuinely one of {@link Gender}'s members. Several call sites read
 * `gender` off a type that only promises `string` (e.g. an admin list projection), so narrowing with
 * a cast would silently accept a value outside the enum instead of falling back.
 */
export const isGender = (value: unknown): value is Gender => value === Gender.Male || value === Gender.Female;

/** Human-readable labels used for clubs: "Men" / "Women". */
export const CLUB_GENDER_LABELS: Record<Gender, string> = {
  [Gender.Male]: 'Men',
  [Gender.Female]: 'Women',
};

/** Select options for picking a club's gender, label shown as "Men" / "Women". */
export const CLUB_GENDER_OPTIONS: ReadonlyArray<{ value: Gender; label: string }> = [
  { value: Gender.Male, label: CLUB_GENDER_LABELS[Gender.Male] },
  { value: Gender.Female, label: CLUB_GENDER_LABELS[Gender.Female] },
];

/** Label ("Men"/"Women") for a club's gender, defaulting to "Men" when unset. */
export const getClubGenderLabel = (gender?: Gender | null): string => CLUB_GENDER_LABELS[gender ?? Gender.Male];

// User Credentials Deploy Types
export type DeployUserConflict = {
  tenantId: string;
  tenantName: string;
  existingUserId?: string;
  existingUserEmail?: string;
  hasConflict: boolean;
};

export type DeployUserCheckResult = {
  conflicts: DeployUserConflict[];
  noConflicts: string[];
};

export type DeployUserResultItem = {
  tenantId: string;
  tenantName: string;
  status: 'created' | 'updated' | 'skipped' | 'error';
  userId?: string;
  error?: string;
};

export type DeployUserResult = {
  userId: string;
  email: string;
  results: DeployUserResultItem[];
};
